import { AddIcon } from "@minket/brand/icons/add";
import { InboundIcon } from "@minket/brand/icons/inbound";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense, useState } from "react";
import { type InboundDialog, InboundDialogHost } from "@/components/inbound/host";
import type { ForwardAddress, ReceivingDomain } from "@/components/inbound/model";
import {
  ForwardingTable,
  MessagesTable,
  ReceivingTable,
  RoutesTable,
} from "@/components/inbound/tables";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { InspectorLayout, useLedger } from "@/components/page/inspector";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { Notice, QueryNotice } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { type BodyOf, endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/inbound/")({
  component: Inbound,
});

/** The message in view loads with its first showing (it reads the message's detail). */
const MessageInspector = lazy(() =>
  import("@/components/inbound/inspector.ondemand").then((m) => ({ default: m.MessageInspector })),
);

const messageColumns = ["Status", "Message", "Received for", "Verdicts", "Received, UTC"];

function Inbound() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const canManage = useCan("inbound.manage");
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<InboundDialog | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const enabled = !!project;
  const receiving = useQuery({ ...apiQuery(endpoints.receiving, { projectId }), enabled });
  const forwarding = useQuery({ ...apiQuery(endpoints.forwarding, { projectId }), enabled });
  const refresh = (endpoint: Parameters<typeof apiKey>[0]) =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoint, { projectId }) });
  const failed = (error: unknown) => notify("error", describeError(error).message);

  const saveRoute = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id?: string;
      body: BodyOf<typeof endpoints.createInboundRoute>;
    }) => {
      if (!id) return apiRequest(endpoints.createInboundRoute, { projectId }, { body });
      const { domain_id: _domain, ...changes } = body;
      return apiRequest(
        endpoints.updateInboundRoute,
        { projectId, routeId: id },
        { body: changes },
      );
    },
    onSuccess: (_route, input) => {
      refresh(endpoints.inboundRoutes);
      setDialog(null);
      notify("success", input.id ? "Rule saved." : "Rule added.");
    },
  });
  const deleteRoute = useMutation({
    mutationFn: (routeId: string) =>
      apiRequest(endpoints.deleteInboundRoute, { projectId, routeId }),
    onSuccess: () => {
      refresh(endpoints.inboundRoutes);
      setDialog(null);
      notify("success", "Rule deleted.");
    },
  });
  const addAddress = useMutation({
    mutationFn: (address: string) =>
      apiRequest(endpoints.addForwarding, { projectId }, { body: { address } }),
    onSuccess: (address) => {
      refresh(endpoints.forwarding);
      setDialog(null);
      notify(
        "success",
        address.status === "confirmed"
          ? "Already confirmed."
          : `Request sent to ${address.address}.`,
      );
    },
    onError: (error) => (dialog ? undefined : failed(error)),
    onSettled: () => setBusy(null),
  });
  const removeAddress = useMutation({
    mutationFn: (address: ForwardAddress) =>
      apiRequest(endpoints.removeForwarding, { projectId, addressId: address.id }),
    onSuccess: () => {
      refresh(endpoints.forwarding);
      notify("success", "Address removed.");
    },
    onError: failed,
    onSettled: () => setBusy(null),
  });
  const check = useMutation({
    mutationFn: (domain: ReceivingDomain) =>
      apiRequest(endpoints.checkReceiving, { projectId, domainId: domain.domain_id }),
    onSuccess: () => refresh(endpoints.receiving),
    onError: failed,
    onSettled: () => setBusy(null),
  });
  const toggle = useMutation({
    mutationFn: (domain: ReceivingDomain) =>
      apiRequest(
        endpoints.updateDomain,
        { projectId, domainId: domain.domain_id },
        { body: { receiving: !domain.receiving } },
      ),
    onSuccess: (domain) => {
      refresh(endpoints.receiving);
      notify(
        "success",
        domain.receiving ? `Receiving on for ${domain.name}.` : `Receiving off for ${domain.name}.`,
      );
    },
    onError: failed,
    onSettled: () => setBusy(null),
  });

  const domains = receiving.data?.data ?? [];
  const action =
    !project || !canManage || domains.length === 0 ? (
      <UnavailableAction
        label="Add route"
        icon={AddIcon}
        reason={
          !project
            ? "Pick a project first"
            : !canManage
              ? "Viewers cannot change routing"
              : "Add a domain first"
        }
      />
    ) : (
      <Button onClick={() => setDialog({ kind: "route" })}>
        <AddIcon className="size-5" />
        Add route
      </Button>
    );

  return (
    <Page title="Received" action={action}>
      <ProjectScope
        will="Received messages will be listed here with their verdicts, beside the rules that route them."
        icon={<InboundIcon />}
        preview={(busy) => (
          <GhostTable columns={messageColumns} label="inbound messages" busy={busy} />
        )}
      >
        {(current) => (
          <Sheet>
            <Messages projectId={current.id} />
            <RoutesSection
              projectId={current.id}
              domains={domains}
              canManage={canManage}
              open={setDialog}
            />
            <Section
              wide
              title="Forwarding addresses"
              description="Rules forward only to addresses whose owner replied from them, and only mail that passed the spam check."
              action={
                canManage && domains.length > 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDialog({ kind: "address" })}
                  >
                    Add address
                  </Button>
                ) : null
              }
            >
              <QueryNotice query={forwarding} noun="forwarding addresses">
                {(list) =>
                  list.data.length === 0 ? (
                    <Notice>No forwarding addresses yet.</Notice>
                  ) : (
                    <ForwardingTable
                      addresses={list.data}
                      canManage={canManage}
                      busy={busy}
                      onResend={(address) => {
                        setBusy(address.id);
                        addAddress.mutate(address.address);
                      }}
                      onRemove={(address) => {
                        setBusy(address.id);
                        removeAddress.mutate(address);
                      }}
                    />
                  )
                }
              </QueryNotice>
            </Section>
            <Section wide title="Receiving domains">
              <QueryNotice query={receiving} noun="receiving domains">
                {(list) =>
                  list.data.length === 0 ? (
                    <Notice>
                      No domains yet. Add one under Domains, then turn receiving on here.
                    </Notice>
                  ) : (
                    <ReceivingTable
                      domains={list.data}
                      canManage={canManage}
                      busy={busy}
                      onCheck={(domain) => {
                        setBusy(domain.domain_id);
                        check.mutate(domain);
                      }}
                      onToggle={(domain) => {
                        setBusy(domain.domain_id);
                        toggle.mutate(domain);
                      }}
                    />
                  )
                }
              </QueryNotice>
            </Section>
          </Sheet>
        )}
      </ProjectScope>
      {dialog ? (
        <InboundDialogHost
          dialog={dialog}
          projectId={projectId}
          domains={domains}
          addresses={forwarding.data?.data ?? []}
          onClose={() => {
            setDialog(null);
            saveRoute.reset();
            deleteRoute.reset();
            addAddress.reset();
          }}
          saveRoute={{
            run: saveRoute.mutate,
            pending: saveRoute.isPending,
            error: saveRoute.error,
          }}
          deleteRoute={{
            run: deleteRoute.mutate,
            pending: deleteRoute.isPending,
            error: deleteRoute.error,
          }}
          addAddress={{
            run: addAddress.mutate,
            pending: addAddress.isPending,
            error: addAddress.error,
          }}
        />
      ) : null}
    </Page>
  );
}

function Messages({ projectId }: { projectId: string }) {
  const [after, setAfter] = useState<string | undefined>();
  const list = useQuery(apiQuery(endpoints.inbound, { projectId }, { limit: 25, after }));
  const items = list.data?.data ?? [];
  const ledger = useLedger(items.map((message) => message.id));
  let pager: ReactNode = null;
  const last = items.at(-1);
  if (after || list.data?.has_more) {
    pager = (
      <span className="flex flex-wrap gap-2">
        {after ? (
          <Button variant="outline" size="sm" onClick={() => setAfter(undefined)}>
            Newest
          </Button>
        ) : null}
        {list.data?.has_more && last ? (
          <Button variant="outline" size="sm" onClick={() => setAfter(last.id)}>
            Older
          </Button>
        ) : null}
      </span>
    );
  }
  return (
    <Section wide title="Messages">
      <QueryNotice query={list} noun="inbound messages">
        {() =>
          items.length === 0 ? (
            <Notice>
              No mail received yet. Once a domain's MX record points to SES, its mail shows here.
            </Notice>
          ) : (
            <InspectorLayout
              ledger={ledger}
              inspector={(id) => {
                const message = items.find((item) => item.id === id);
                return message ? (
                  <Suspense fallback={null}>
                    <MessageInspector
                      projectId={projectId}
                      message={message}
                      onClose={ledger.close}
                    />
                  </Suspense>
                ) : null;
              }}
            >
              <div className="grid min-w-0 gap-3">
                <MessagesTable messages={items} ledger={ledger} />
                {pager}
              </div>
            </InspectorLayout>
          )
        }
      </QueryNotice>
    </Section>
  );
}

function RoutesSection({
  projectId,
  domains,
  canManage,
  open,
}: {
  projectId: string;
  domains: ReceivingDomain[];
  canManage: boolean;
  open: (dialog: InboundDialog) => void;
}) {
  const routes = useQuery(apiQuery(endpoints.inboundRoutes, { projectId }));
  return (
    <Section
      wide
      title="Routing rules"
      description="Mail no rule takes goes to every endpoint subscribed to email.received."
    >
      <QueryNotice query={routes} noun="routing rules">
        {(list) =>
          list.data.length === 0 ? (
            <Notice>No routing rules yet.</Notice>
          ) : (
            <RoutesTable
              routes={list.data}
              domainName={(id) => domains.find((d) => d.domain_id === id)?.name}
              canManage={canManage}
              onEdit={(route) => open({ kind: "route", route })}
              onDelete={(route) => open({ kind: "delete", route })}
            />
          )
        }
      </QueryNotice>
    </Section>
  );
}
